import {
  backgroundCheckPath,
  communicationPath,
  registrationPath,
  schedulingPath,
  signUpPath,
  websiteBuilderPath,
} from "@/constants/paths";
import { css } from "@emotion/react";
import Link from "next/link";
import Button from "../Buttons/Button";
import Container from "../Container/Container";
import { H3Css, H5Css, H7Css, Heading } from "../Typography";
import Balancer from "react-wrap-balancer";
import { useMemo } from "react";
import websiteIcon from "../../../public/images/blog-post/icon/desktop.svg";
import registrationIcon from "../../../public/images/blog-post/icon/card.svg";
import communicationIcon from "../../../public/images/blog-post/icon/communication.svg";
import schedulesIcon from "../../../public/images/blog-post/icon/calendar.svg";
import backgroundChecksIcon from "../../../public/images/blog-post/icon/background-check.svg";
import Image from "next/image";
import { mq } from "@/constants/breakpoints";

interface RightLinkProps {
  type:
    | "website"
    | "registration"
    | "communication"
    | "schedules"
    | "backgroundChecks";
}

const RightLink = ({ type }: RightLinkProps) => {
  const { iconSrc, title, href } = useMemo(() => {
    let iconSrc;
    let title;
    let href;
    switch (type) {
      case "website":
        iconSrc = websiteIcon;
        title = "Lightning Fast Website Builder";
        href = websiteBuilderPath;
        break;
      case "registration":
        iconSrc = registrationIcon;
        title = "Powerful Registration & Payments Features";
        href = registrationPath;
        break;
      case "communication":
        iconSrc = communicationIcon;
        title = "Text & Email Communication Tools";
        href = communicationPath;
        break;
      case "schedules":
        iconSrc = schedulesIcon;
        title = "Team & Organization Schedules";
        href = schedulingPath;
        break;
      case "backgroundChecks":
        iconSrc = backgroundChecksIcon;
        title = "Fast & Reliable Background Checks";
        href = backgroundCheckPath;
        break;
    }
    return { iconSrc, title, href };
  }, [type]);

  return (
    <Link
      href={href}
      css={css`
        &:hover {
          .rightTitle {
            text-decoration-color: #fff;
          }
        }
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 18px;
        `}
      >
        <div
          css={css`
            min-width: 50px;
            width: 50px;
            height: 50px;
            min-height: 50px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(
              129.16deg,
              rgba(255, 255, 255, 0.4) -68.9%,
              rgba(190, 220, 255, 0.4) 131.19%
            );
            backdrop-filter: blur(8.5px);

            @supports (-webkit-backdrop-filter: none) {
              -webkit-backdrop-filter: none;
            }
          `}
        >
          <Image src={iconSrc} alt={title} />
        </div>
        <Heading
          className="rightTitle"
          css={css`
            color: #fff;
            text-decoration-line: underline;
            text-decoration-color: transparent;
            text-decoration-thickness: 2px;
            text-underline-offset: 4px;
            transition: all 0.2s ease-in-out;
            font-weight: 400;
            font-size: 16px;
            line-height: 140%;
            ${mq["lg"]} {
              ${H7Css}
            }
          `}
        >
          {title}
        </Heading>
      </div>
    </Link>
  );
};

interface BlogSignupProps {}

const BlogSignup = ({}: BlogSignupProps) => {
  return (
    <div
      id="signup-cta-container"
      css={css`
        background: url("/images/blog-post/BG.svg"),
          linear-gradient(112.24deg, #048cdd 4.51%, #32b2ff 100.81%);
        background-repeat: no-repeat;
        background-size: cover;
      `}
    >
      <Container>
        <div
          css={css`
            padding: 60px 0;
            display: flex;
            flex-direction: column;
            gap: 30px;
            justify-content: space-between;
            align-items: center;
            box-sizing: border-box;
            ${mq["md"]} {
              flex-direction: row;
            }
            ${mq["lg"]} {
              padding: 100px 0;
            }
          `}
        >
          <div
            css={css`
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 28px;
              ${mq["lg"]} {
                gap: 40px;
              }
            `}
          >
            <Heading
              css={css`
                color: #fff;
                text-align: center;
                ${H5Css}
                ${mq["md"]} {
                  text-align: left;
                }
                ${mq["lg"]} {
                  ${H3Css}
                }
              `}
            >
              <Balancer>
                Jersey Watch is the Fastest Way to Manage Your Sports
                Organization
              </Balancer>
            </Heading>
            <Link
              href={signUpPath}
              css={css`
                display: block;
                width: fit-content;
                margin: auto;
                ${mq["md"]} {
                  margin: unset;
                }
              `}
            >
              <Button
                jwType="light"
                id="try-jersey-watch-for-free-blog"
                css={css`
                  width: 270px;
                `}
              >
                Try Jersey Watch For Free
              </Button>
            </Link>
          </div>
          <div
            css={css`
              flex: 1;
              background: linear-gradient(
                103.92deg,
                rgba(255, 255, 255, 0.15) -0.74%,
                rgba(190, 220, 255, 0.15) 99.87%
              );
              backdrop-filter: blur(39.206px);
              border-radius: 20px;
              padding: 24px;
              display: flex;
              flex-direction: column;
              width: 100%;
              max-width: 580px;
              gap: 24px;
              box-sizing: border-box;
              ${mq["md"]} {
                padding: 40px;
                gap: 40px;
              }
            `}
          >
            <RightLink type="website" />
            <RightLink type="registration" />
            <RightLink type="communication" />
            <RightLink type="schedules" />
            <RightLink type="backgroundChecks" />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default BlogSignup;
